// src/utils/seasonUtils.js - Frontend season utilities that fetch from backend
import api from '../services/apiService';

// Cache for season info to avoid repeated API calls
let seasonCache = null;
let cachePromise = null;

/**
 * Fetch current season from backend API.
 * This is the SINGLE SOURCE OF TRUTH.
 * The backend determines current season based on actual game data.
 */
const fetchCurrentSeasonFromBackend = async () => {
  try {
    const response = await api.get('/api/leagues/season-info');
    return response.data.current_season;
  } catch (error) {
    console.error('Error fetching current season from backend:', error);
    // Fallback to calendar-based logic if API fails
    return getCalendarBasedSeason();
  }
};

/**
 * Fallback: Calendar-based season logic (only used if API fails).
 * This should match backend's core/season_utils.py logic.
 */
const getCalendarBasedSeason = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1; // 1-indexed (1 = January)
  
  // Before March, we're still in last year's season (matches backend season_utils.py)
  if (month < 3) {
    return year - 1;
  }
  return year;
};

/**
 * Get current season - fetches from backend on first call, then caches.
 * Returns a Promise that resolves to the season number.
 */
export const getCurrentSeasonAsync = async () => {
  // If we have a cached value, return it
  if (seasonCache !== null) {
    return seasonCache;
  }
  
  // If we're already fetching, return the existing promise
  if (cachePromise) {
    return cachePromise;
  }
  
  // Fetch from backend and cache the promise
  cachePromise = fetchCurrentSeasonFromBackend().then(season => {
    seasonCache = season;
    cachePromise = null;
    return season;
  });
  
  return cachePromise;
};

/**
 * Get current season synchronously.
 * WARNING: This uses cached value or fallback.
 * For initial load, use getCurrentSeasonAsync() instead.
 */
export const getCurrentSeason = () => {
  // If we have cached value, return it
  if (seasonCache !== null) {
    return seasonCache;
  }
  
  // Fallback to calendar-based (this should rarely happen)
  console.warn('getCurrentSeason() called before async fetch completed, using fallback');
  return getCalendarBasedSeason();
};

/**
 * Initialize season cache on app startup.
 * Call this from App.js or main entry point.
 */
export const initializeSeasonCache = async () => {
  await getCurrentSeasonAsync();
};

/**
 * Clear the season cache (useful for testing or after season rollover).
 */
export const clearSeasonCache = () => {
  seasonCache = null;
  cachePromise = null;
};

export const getSeasonDateRange = (season) => {
  return {
    start: new Date(season, 2, 1), // March 1st
    end: new Date(season, 9, 31)   // October 31st
  };
};

export const isCurrentSeason = (season) => {
  return season === getCurrentSeason();
};

export const formatSeason = (season) => {
  return `${season} Season`;
};